import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Modal, PageHeader, Badge, Spinner, EmptyState, Paginator } from '../components/ui';
import { ROLES, SUB_ROLES, INDIA_STATES } from '../utils/constants';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

const ROLE_COLORS = { super_admin: 'purple', admin: 'navy', internal_team: 'blue' };

export default function TeamPage() {
  const { user: me, isAdmin, isSuperAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'internal_team', sub_role: 'Intern',
    region: '', regions: [], manager_id: ''
  });

  const load = async () => {
    try {
      const res = await usersAPI.getAll();
      setMembers(res.data?.data?.users || []);
    } catch { toast.error('Failed to load team'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditMember(null);
    setForm({ name: '', email: '', password: '', role: 'internal_team', sub_role: 'Intern', region: '', regions: [], manager_id: '' });
    setModal(true);
  };

  const openEdit = (m) => {
    setEditMember(m);
    setForm({ name: m.name, email: m.email, password: '', role: m.role, sub_role: m.sub_role || '', region: m.region || '', regions: m.regions || [], manager_id: m.manager_id?._id || '' });
    setModal(true);
  };

  const save = async () => {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    if (!editMember && !form.password) { toast.error('Password required for new member'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.manager_id) delete payload.manager_id;
      if (editMember) {
        await usersAPI.update(editMember._id, payload);
        toast.success('Member updated');
      } else {
        await usersAPI.create(payload);
        toast.success('Member added');
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const toggleActive = async (m) => {
    try {
      await usersAPI.update(m._id, { is_active: !m.is_active });
      setMembers(ms => ms.map(x => x._id === m._id ? { ...x, is_active: !x.is_active } : x));
      toast.success(m.is_active ? 'Member deactivated' : 'Member activated');
    } catch { toast.error('Failed to update'); }
  };

  const filtered = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.sub_role?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const managers = members.filter(m => m.sub_role === 'Manager' || m.sub_role === 'SVP' || m.sub_role === 'MD' || m.role === 'admin' || m.role === 'super_admin');

  return (
    <div>
      <PageHeader
        title="Team"
        subtitle={`${members.filter(m => m.is_active).length} active members`}
        actions={<Button variant="primary" size="sm" onClick={openCreate}>+ Add Member</Button>}
      />

      <Card style={{ marginBottom: 16, padding: '10px 14px' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name, email, role…" style={{ ...inp, maxWidth: 300 }} />
      </Card>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="👥" title="No team members" subtitle="Add your first team member above" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {paged.map(m => (
            <Card key={m._id} style={{ padding: '14px 16px', opacity: m.is_active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, background: m.is_active ? '#1B2A6B' : '#94a3b8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: '#fff', flexShrink: 0 }}>
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</p>
                    {m._id === me?._id && <span style={{ fontSize: 10, background: '#C9A84C', color: '#1B2A6B', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>YOU</span>}
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <Badge color={ROLE_COLORS[m.role] || 'gray'}>{m.role.replace('_', ' ')}</Badge>
                    {m.sub_role && <Badge color="gold">{m.sub_role}</Badge>}
                    {m.sub_role === 'SVP' && m.regions?.length > 0
                      ? m.regions.map(r => <Badge key={r} color="gray">📍 {r}</Badge>)
                      : m.region && <Badge color="gray">📍 {m.region}</Badge>
                    }
                    {!m.is_active && <Badge color="red">Inactive</Badge>}
                  </div>
                  {m.manager_id && (
                    <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Reports to: {m.manager_id?.name || '—'}</p>
                  )}
                </div>
              </div>
              {m._id !== me?._id && (isSuperAdmin || m.role !== 'super_admin') && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                  <Button size="sm" variant="outline" onClick={() => openEdit(m)} style={{ flex: 1, justifyContent: 'center' }}>Edit</Button>
                  <Button size="sm" variant={m.is_active ? 'danger' : 'ghost'} onClick={() => toggleActive(m)} style={{ flex: 1, justifyContent: 'center' }}>
                    {m.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Paginator page={page} totalPages={totalPages} onPageChange={setPage} />

      <Modal open={modal} onClose={() => setModal(false)} title={editMember ? 'Edit Member' : 'Add Team Member'}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Full Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>{editMember ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={inp} placeholder="••••••••" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={sel}>
                <option value="internal_team">Internal Team</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Sub-Role</label>
              <select value={form.sub_role} onChange={e => setForm(f => ({ ...f, sub_role: e.target.value }))} style={sel}>
                <option value="">None</option>
                {SUB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={form.sub_role === 'SVP' ? { gridColumn: '1 / -1' } : {}}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                {form.sub_role === 'SVP' ? 'Regions (multi-select)' : 'Region'}
              </label>
              {form.sub_role === 'SVP' ? (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', maxHeight: 160, overflowY: 'auto' }}>
                    {INDIA_STATES.map(s => {
                      const checked = form.regions.includes(s);
                      return (
                        <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', padding: '3px 8px', borderRadius: 6, background: checked ? '#1B2A6B' : '#f1f5f9', color: checked ? '#fff' : '#475569', fontWeight: checked ? 600 : 400, userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            style={{ display: 'none' }}
                            onChange={() => setForm(f => ({
                              ...f,
                              regions: checked ? f.regions.filter(r => r !== s) : [...f.regions, s]
                            }))}
                          />
                          {s}
                        </label>
                      );
                    })}
                  </div>
                  {form.regions.length > 0 && (
                    <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{form.regions.length} region{form.regions.length > 1 ? 's' : ''} selected</p>
                  )}
                </>
              ) : (
                <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} style={sel}>
                  <option value="">All India</option>
                  {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Reports To (Manager)</label>
            <select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))} style={sel}>
              <option value="">None</option>
              {managers.map(m => <option key={m._id} value={m._id}>{m.name} ({m.sub_role || m.role})</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : editMember ? 'Update' : 'Add Member'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
